import { useState, type ReactNode } from "react";
import {
  BackLink,
  Button,
  Container,
  PortalLayout,
  PortalPanel,
  PortalPanelBody,
  ResponsiveText,
  SectionCard,
  Stack,
  StatusPanel,
  Stepper,
  Typography,
} from "../../design-system";
import type { StepperItemData } from "../../design-system";
import { renewalCaptureContent as content } from "./content";
import { exposureContent } from "./exposure-content";
import { AdditionalDetailsPage, ExposureQuestionsEngine } from "./ExposureQuestionsEngine";
import { ReadOnlyFieldset } from "./ReadOnlyFieldset";
import type { ExposureAnswers } from "./exposure-answers";
import type { ExposureSectionSchema } from "./exposure-schema";
import type { DetailsPageRow, ExposureErrorSummaryItem } from "./exposure-validation";
import type { OnFileRenderContext } from "./on-file-render";

export interface SectionPageRouteProps {
  readonly businessName: string;
  /** H2 04-05-02: the page header is the step's own ("Review your exposures"
   * for an owner, "Review your sections" for a colleague), the same as the
   * list view; the section title is the card's own h2. Defaults to owner. */
  readonly isOwner?: boolean;
  readonly stepperItems?: readonly StepperItemData[];
  readonly title: string;
  readonly policyNumber?: string;
  readonly order: number;
  readonly total: number;
  /** The section's full question schema, omitted for an additional-details page, which renders
   * `additionalDetails` instead (never the parent section's own questions again). */
  readonly schema?: ExposureSectionSchema;
  /** H2 additional-details page: the parent row, the row's title, and the help behind the
   * "Why are we asking for this?" trigger beside the page title. */
  readonly additionalDetails?: {
    readonly parent: DetailsPageRow;
    readonly title: string;
    readonly helpText: string;
  };
  readonly answers: ExposureAnswers;
  readonly onChange: (key: string, value: unknown) => void;
  readonly errors: readonly ExposureErrorSummaryItem[];
  /** TG-5 (m1-exposure-detail-sourcing): this section instance's own
   * on-file read, threaded straight through to `ExposureQuestionsEngine`
   * -- omitted for an additional-details page, which renders no data-
   * on-file question at all. */
  readonly onFile?: OnFileRenderContext;
  readonly onContinue: () => void;
  /** H2 README.md:241: "Back" (secondary), shown "from section 2" --
   * omitted entirely on the first section/page of the ordered list, where
   * there is nothing to go back to. Opens the previous section/page the
   * same way opening one from the list does today -- no save, no
   * validation. */
  readonly onPrevious?: () => void;
  readonly onSaveAndFinishLater?: () => void;
  readonly onBackToList: () => void;
  /** AC-18: an owner viewing a section Assigned to a colleague (not yet
   * completed) sees it inside a disabled fieldset, legend "Read only",
   * and Continue returns to the list without persisting. */
  readonly readOnly?: boolean;
  /** AC-18: the help panel shown above a read-only or colleague-completed
   * section. */
  readonly statusPanelParagraphs?: readonly string[];
  readonly isSaving?: boolean;
  readonly saveOutcome?: ReactNode;
  /** OBJ-112/OBJ-114 carried forward: while a post-write reload is
   * failing, the banner shows above the page and Continue is disabled so
   * no write is made against a snapshot the portal cannot confirm is
   * current. Entered answers stay on screen. */
  readonly reloadFailureBanner?: ReactNode;
  /** AC-25: the owner's text action on the right of the section title. */
  readonly headerAction?: { readonly label: string; readonly onClick: () => void; readonly testId: string };
}

interface SectionQuestionsProps {
  readonly schema: ExposureSectionSchema | undefined;
  readonly additionalDetails: SectionPageRouteProps["additionalDetails"];
  readonly answers: ExposureAnswers;
  readonly onChange: (key: string, value: unknown) => void;
  readonly errors: readonly ExposureErrorSummaryItem[];
  readonly disabled?: boolean;
  readonly onFile?: OnFileRenderContext;
}

/**
 * ADR-20261016-n4sb (SonarQube CRITICAL typescript:S3776 remediation):
 * the one question-rendering choice `SectionPageRoute` needs — an
 * additional-details page's flat field list, or a full section schema, or
 * neither — previously appeared twice as a duplicated, four-way nested
 * ternary (once for the read-only branch, once for the editable one),
 * which is exactly what drove this component's cognitive-complexity
 * score over the gate's threshold. Pulling it out to its own named
 * function removes both the duplication and the nesting from
 * `SectionPageRoute` itself; this function's own complexity is a single,
 * flat two-branch early-return, nowhere near the threshold. `disabled`
 * defaults to `false`, matching the editable call site's prior omission
 * of the prop entirely.
 */
function SectionQuestions({
  schema,
  additionalDetails,
  answers,
  onChange,
  errors,
  disabled = false,
  onFile,
}: Readonly<SectionQuestionsProps>) {
  const onFileProp = onFile !== undefined ? { onFile } : {};
  if (additionalDetails !== undefined) {
    return (
      <AdditionalDetailsPage
        parent={additionalDetails.parent}
        title={additionalDetails.title}
        answers={answers}
        onChange={onChange}
        errors={errors}
        disabled={disabled}
      />
    );
  }
  if (schema !== undefined) {
    return (
      <ExposureQuestionsEngine
        schema={schema}
        answers={answers}
        onChange={onChange}
        errors={errors}
        disabled={disabled}
        {...onFileProp}
      />
    );
  }
  return null;
}

/**
 * H2 screens 5 (and the per-item additional-details pages, which this
 * same component renders — `schema` for one carries only that page's own
 * fields; AC-15). `readOnly` wraps the engine in `ReadOnlyFieldset`
 * (AC-18) rather than threading a `disabled` prop through every question
 * type, matching the native-fieldset mechanism's own "every descendant
 * control" guarantee.
 */
export function SectionPageRoute({
  businessName,
  title,
  policyNumber,
  order,
  total,
  schema,
  additionalDetails,
  answers,
  onChange,
  errors,
  onFile,
  onContinue,
  onPrevious,
  onSaveAndFinishLater,
  onBackToList,
  readOnly = false,
  statusPanelParagraphs,
  isSaving = false,
  saveOutcome,
  reloadFailureBanner = null,
  isOwner = true,
  stepperItems,
  headerAction,
}: Readonly<SectionPageRouteProps>) {
  const isStale = reloadFailureBanner !== null;
  const onFileProp = onFile !== undefined ? { onFile } : {};
  const [exposuresHelpOpen, setExposuresHelpOpen] = useState(false);
  const [detailsHelpOpen, setDetailsHelpOpen] = useState(false);
  const detailsHelpPanel =
    additionalDetails !== undefined ? (
      <PortalPanelBody id="section-page-details-help" expanded={detailsHelpOpen} spaced>
        {additionalDetails.helpText}
      </PortalPanelBody>
    ) : null;
  const statusPanel =
    statusPanelParagraphs !== undefined && statusPanelParagraphs.length > 0 ? (
      <StatusPanel paragraphs={statusPanelParagraphs} h2Panel testId="section-page-status-panel" />
    ) : null;
  return (
    <PortalLayout businessName={businessName}>
      <Container layout="exposures">
        <>
          {reloadFailureBanner}
          {isOwner && stepperItems !== undefined ? (
            <Stepper aria-label="Renewal steps" items={stepperItems} testId="section-page-stepper" />
          ) : null}
          {/* H2 (04-05-GuidedExposures.dc.html, README.md "Back link -- above
              the card"): the back link sits above the card, never inside
              it. */}
          <BackLink label={content.backToSectionsLabel} onClick={onBackToList} testId="section-page-back-to-list" />

          {/* H2 04-05-02 (README.md:183-194): the section view shares the list
              view's header -- eyebrow, "Review your exposures" with the
              "What are exposures?" help beside it (not inside the h1), the
              section-view intro, then the help panel. */}
          <Stack gap="heading">
            {isOwner ? (
              <Typography variant="eyebrow" as="p">
                {content.sectionListEyebrow}
              </Typography>
            ) : null}
            <Stack direction="row" align="center" gap="none" wrap>
              <Typography variant="display" as="h1">
                {isOwner ? (
                  <ResponsiveText narrow={content.sectionListHeadingNarrow} wide={content.sectionListHeading} />
                ) : (
                  content.contributorSectionsHeading
                )}
              </Typography>
              {isOwner ? (
                <PortalPanel
                  id="section-page-exposures-help"
                  triggerLabel={content.exposuresHelpTriggerLabel}
                  expanded={exposuresHelpOpen}
                  onToggle={() => setExposuresHelpOpen((open) => !open)}
                />
              ) : null}
            </Stack>
            <Typography variant="bodyLarge">
              {isOwner ? content.sectionPageIntroOwner : content.sectionPageIntroContributor}
            </Typography>
            {isOwner ? (
              <PortalPanelBody id="section-page-exposures-help" expanded={exposuresHelpOpen}>
                {content.exposuresHelpText}
              </PortalPanelBody>
            ) : null}
          </Stack>

          <SectionCard
            counter={content.sectionOfLabel(order, total)}
            title={title}
            {...(policyNumber !== undefined ? { policyRef: content.policyNumberLabel(policyNumber) } : {})}
            {...(headerAction !== undefined ? { action: headerAction } : {})}
            {...(additionalDetails !== undefined
              ? {
                  titleAction: (
                    <PortalPanel
                      size="title"
                      id="section-page-details-help"
                      triggerLabel={exposureContent.detailsHelpTriggerLabel}
                      expanded={detailsHelpOpen}
                      onToggle={() => setDetailsHelpOpen((open) => !open)}
                    />
                  ),
                }
              : {})}
            {...(statusPanel !== null || detailsHelpPanel !== null
              ? {
                  headExtra: (
                    <>
                      {detailsHelpPanel}
                      {statusPanel}
                    </>
                  ),
                }
              : {})}
          >

            {readOnly ? (
              <ReadOnlyFieldset testId="section-page-read-only">
                <SectionQuestions
                  schema={schema}
                  additionalDetails={additionalDetails}
                  answers={answers}
                  onChange={() => {}}
                  errors={[]}
                  disabled
                  {...onFileProp}
                />
              </ReadOnlyFieldset>
            ) : (
              <>
                {/* Fix B (m1-portal-ux-fixes): the save-outcome panel renders
                    at the top of the card's content, before the questions,
                    same placement ConfirmDetailsRoute/DeclarationRoute use. */}
                {saveOutcome}

                <SectionQuestions
                  schema={schema}
                  additionalDetails={additionalDetails}
                  answers={answers}
                  onChange={onChange}
                  errors={errors}
                  {...onFileProp}
                />

                <Stack direction="sectionActions" gap="sm">
                  <Button emphasis="primary" size="large" onClick={onContinue} disabled={isSaving || isStale} testId="section-page-continue">
                    {content.continueLabel}
                  </Button>
                  {onPrevious !== undefined ? (
                    <Button emphasis="secondary" size="large" onClick={onPrevious} testId="section-page-previous">
                      {content.previousSectionLabel}
                    </Button>
                  ) : null}
                  {onSaveAndFinishLater !== undefined ? (
                    <Button emphasis="quiet" size="large" onClick={onSaveAndFinishLater} testId="section-page-save-and-finish-later">
                      {content.saveAndFinishLaterLabel}
                    </Button>
                  ) : null}
                </Stack>
              </>
            )}

            {readOnly ? (
              <Stack direction="sectionActions" gap="sm">
                <Button emphasis="primary" size="large" onClick={onBackToList} testId="section-page-read-only-continue">
                  {content.continueLabel}
                </Button>
                {onPrevious !== undefined ? (
                  <Button emphasis="secondary" size="large" onClick={onPrevious} testId="section-page-previous">
                    {content.previousSectionLabel}
                  </Button>
                ) : null}
                {/* H2 4&5 read only: Save and finish later stays available. */}
                {onSaveAndFinishLater !== undefined ? (
                  <Button emphasis="quiet" size="large" onClick={onSaveAndFinishLater} testId="section-page-read-only-save-and-finish-later">
                    {content.saveAndFinishLaterLabel}
                  </Button>
                ) : null}
              </Stack>
            ) : null}
            {/* H2 4&5: the help line closes the section card. */}
            <Typography variant="bodySmall" tone="muted">
              {isOwner ? content.needHelpOwner : content.needHelpContributor}
            </Typography>
          </SectionCard>
        </>
      </Container>
    </PortalLayout>
  );
}

export function SectionPageErrorAlert({ title, message }: Readonly<{ title: string; message: string }>) {
  return (
    <PortalPanelBody id="section-page-error" expanded tone="error" title={title} testId="section-page-error">
      <Typography variant="body">{message}</Typography>
    </PortalPanelBody>
  );
}
